import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
 Settings, Plus, FileText, Package, Bell, Shield, GitBranch,
 MoreHorizontal, Edit, Trash2, Clock,
} from 'lucide-react';
import CustomFieldsPanel from './CustomFieldsPanel';

// Types
interface DocumentCategory {
 id: number;
 name: string;
 color: string;
 retention: string;
 permissions: string[];
}

interface AssetCategory {
 id: number;
 name: string;
 color: string;
 depreciationRate: number;
 assignmentRules: string;
}

// Mock data for the tabs that are still in preview. Custom Fields is fully functional and has
// no mock data — it talks to the CustomField API.
const mockDocumentCategories: DocumentCategory[] = [
 { id: 1, name: 'Personal Documents', color: '#10b981', retention: '7years', permissions: ['employee', 'manager', 'hr'] },
 { id: 2, name: 'Contracts', color: '#3b82f6', retention: '10years', permissions: ['manager', 'hr'] },
 { id: 3, name: 'Certifications', color: '#f59e0b', retention: 'indefinite', permissions: ['employee', 'manager', 'hr'] },
 { id: 4, name: 'Performance Reviews', color: '#ef4444', retention: '5years', permissions: ['manager', 'hr'] },
];

const mockAssetCategories: AssetCategory[] = [
 { id: 1, name: 'IT Equipment', color: '#8b5cf6', depreciationRate: 25, assignmentRules: 'manager-approval' },
 { id: 2, name: 'Office Furniture', color: '#06b6d4', depreciationRate: 10, assignmentRules: 'auto-assign' },
 { id: 3, name: 'Vehicles', color: '#f97316', depreciationRate: 20, assignmentRules: 'hr-approval' },
 { id: 4, name: 'Access Cards', color: '#84cc16', depreciationRate: 0, assignmentRules: 'auto-assign' },
];

const settingsTabs = [
 { id: 'custom-fields', label: 'Custom Fields', icon: Plus, comingSoon: false },
 { id: 'document-categories', label: 'Document Categories', icon: FileText, comingSoon: true },
 { id: 'asset-categories', label: 'Asset Categories', icon: Package, comingSoon: true },
 { id: 'notification-rules', label: 'Notification Rules', icon: Bell, comingSoon: true },
 { id: 'role-permissions', label: 'Role Permissions', icon: Shield, comingSoon: true },
 { id: 'approval-workflows', label: 'Approval Workflows', icon: GitBranch, comingSoon: true },
];

export default function CoreHRSettings() {
 const { t } = useTranslation();
 const [activeTab, setActiveTab] = useState('custom-fields');
 const activeIsComingSoon = settingsTabs.find(tab => tab.id === activeTab)?.comingSoon;

 const renderTabContent = () => {
 switch (activeTab) {
 case 'custom-fields':
 return <CustomFieldsPanel />;

 case 'document-categories':
 return (
 <div className="bg-card rounded-xl shadow-sm border border-border p-6">
 <div className="flex items-center justify-between mb-6">
 <div className="flex items-center space-x-3">
 <FileText size={24} className="text-green-600" />
 <div>
 <h3 className="text-lg font-semibold text-foreground">{t('Document Categories')}</h3>
 <p className="text-sm text-muted-foreground">{t('Organize and manage document types')}</p>
 </div>
 </div>
 <button className={`flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg`}>
 <Plus size={16} />
 {t('Add Category')}
 </button>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {mockDocumentCategories.map((category) => (
 <div key={category.id} className="border border-border rounded-lg p-4">
 <div className="flex items-center justify-between mb-3">
 <div className="flex items-center space-x-3">
 <div className="w-4 h-4 rounded-full" style={{ backgroundColor: category.color }} />
 <h4 className="font-medium text-foreground">{category.name}</h4>
 </div>
 <button className={`p-1`}>
 <MoreHorizontal size={16} />
 </button>
 </div>
 <div className="space-y-2">
 <div className="flex items-center justify-between text-sm">
 <span className="text-muted-foreground">{t('Retention Period')}:</span>
 <span className="font-medium text-foreground">{category.retention}</span>
 </div>
 <div className="flex items-center justify-between text-sm">
 <span className="text-muted-foreground">{t('Access')}:</span>
 <div className="flex space-x-1">
 {category.permissions.map((perm) => (
 <span key={perm} className="px-2 py-1 bg-muted text-xs rounded">{perm}</span>
 ))}
 </div>
 </div>
 </div>
 </div>
 ))}
 </div>
 </div>
 );

 case 'asset-categories':
 return (
 <div className="bg-card rounded-xl shadow-sm border border-border p-6">
 <div className="flex items-center justify-between mb-6">
 <div className="flex items-center space-x-3">
 <Package size={24} className="text-purple-600" />
 <div>
 <h3 className="text-lg font-semibold text-foreground">{t('Asset Categories')}</h3>
 <p className="text-sm text-muted-foreground">{t('Configure asset types and assignment rules')}</p>
 </div>
 </div>
 <button className={`flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg`}>
 <Plus size={16} />
 {t('Add Category')}
 </button>
 </div>

 <div className="overflow-x-auto">
 <table className="min-w-full divide-y divide-border">
 <thead className="bg-muted/40">
 <tr>
 <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('Category')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('Depreciation Rate')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('Assignment Rules')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('Actions')}</th>
 </tr>
 </thead>
 <tbody className="bg-card divide-y divide-border">
 {mockAssetCategories.map((category) => (
 <tr key={category.id}>
 <td className="px-6 py-4 whitespace-nowrap">
 <div className="flex items-center space-x-3">
 <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color }} />
 <span className="text-sm font-medium text-foreground">{category.name}</span>
 </div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
 {category.depreciationRate}% per year
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
 {category.assignmentRules.replace('-', ' ')}
 </span>
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
 <div className="flex items-center space-x-2">
 <button className={`flex items-center gap-1 text-muted-foreground`}>
 <Edit size={14} /> {t('Edit')}
 </button>
 <button className={`flex items-center gap-1 text-destructive`}>
 <Trash2 size={14} /> {t('Delete')}
 </button>
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>
 );

 case 'notification-rules':
 return (
 <div className="bg-card rounded-xl shadow-sm border border-border p-6">
 <div className="flex items-center space-x-3 mb-6">
 <Bell size={24} className="text-yellow-600" />
 <div>
 <h3 className="text-lg font-semibold text-foreground">{t('Notification Rules')}</h3>
 <p className="text-sm text-muted-foreground">{t('Configure automatic reminders and alerts')}</p>
 </div>
 </div>

 <div className="space-y-6">
 {[
 { title: 'Contract Expiry Reminders', desc: 'Notify about upcoming contract expirations', enabled: true, days: 30 },
 { title: 'Document Expiry Reminders', desc: 'Alert when documents are about to expire', enabled: true, days: 14 },
 { title: 'Probation End Reminders', desc: 'Notify managers about probation period endings', enabled: true, days: 7 },
 { title: 'Asset Return Reminders', desc: 'Remind employees to return assets', enabled: true, days: 3 },
 ].map((rule, idx) => (
 <div key={idx} className="border border-border rounded-lg p-4">
 <div className="flex items-center justify-between mb-4">
 <div>
 <h4 className="font-medium text-foreground">{t(rule.title, { defaultValue: rule.title })}</h4>
 <p className="text-sm text-muted-foreground">{t(rule.desc, { defaultValue: rule.desc })}</p>
 </div>
 <input type="checkbox" checked={rule.enabled} readOnly className="rounded" />
 </div>
 {rule.enabled && (
 <div className="ms-6">
 <label className="block text-sm font-medium text-muted-foreground mb-2">
 {t('Remind before (days)')}
 </label>
 <input
 type="number"
 value={rule.days}
 readOnly
 className="max-w-xs px-3 py-2 border border-border rounded-md bg-muted/40 text-muted-foreground"
 />
 </div>
 )}
 </div>
 ))}
 </div>
 </div>
 );

 case 'role-permissions':
 return (
 <div className="bg-card rounded-xl shadow-sm border border-border p-6">
 <div className="flex items-center space-x-3 mb-6">
 <Shield size={24} className="text-indigo-600" />
 <div>
 <h3 className="text-lg font-semibold text-foreground">{t('Role Permissions')}</h3>
 <p className="text-sm text-muted-foreground">{t('Configure access levels for different user roles')}</p>
 </div>
 </div>

 <div className="space-y-6">
 {[
 { title: 'Employee Permissions', perms: ['View Own Profile', 'Edit Personal Info', 'View Own Documents', 'Request Changes', 'View Team Directory'] },
 { title: 'Manager Permissions', perms: ['View Team Profiles', 'Approve Requests', 'View Team Reports', 'Assign Assets', 'Performance Reviews'] },
 { title: 'HR Admin Permissions', perms: ['Full Access', 'System Settings', 'Reports Access', 'User Management', 'Audit Logs', 'Data Export'] },
 ].map((section, idx) => (
 <div key={idx} className="border border-border rounded-lg p-4">
 <h4 className="font-medium text-foreground mb-3">{t(section.title, { defaultValue: section.title })}</h4>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 {section.perms.map((permission) => (
 <div key={permission} className="flex items-center space-x-2">
 <input type="checkbox" defaultChecked readOnly className="rounded" />
 <label className="text-sm text-muted-foreground">{t(permission, { defaultValue: permission })}</label>
 </div>
 ))}
 </div>
 </div>
 ))}
 </div>
 </div>
 );

 case 'approval-workflows':
 return (
 <div className="bg-card rounded-xl shadow-sm border border-border p-6">
 <div className="flex items-center space-x-3 mb-6">
 <GitBranch size={24} className="text-teal-600" />
 <div>
 <h3 className="text-lg font-semibold text-foreground">{t('Approval Workflows')}</h3>
 <p className="text-sm text-muted-foreground">{t('Configure approval processes for HR operations')}</p>
 </div>
 </div>

 <div className="space-y-6">
 {[
 { title: 'Contract Changes', desc: 'Require approval for contract modifications', enabled: true },
 { title: 'Salary Updates', desc: 'Approval required for salary changes', enabled: true },
 { title: 'Transfers', desc: 'Approval workflow for employee transfers', enabled: true },
 ].map((workflow, idx) => (
 <div key={idx} className="border border-border rounded-lg p-4">
 <div className="flex items-center justify-between mb-4">
 <div>
 <h4 className="font-medium text-foreground">{t(workflow.title, { defaultValue: workflow.title })}</h4>
 <p className="text-sm text-muted-foreground">{t(workflow.desc, { defaultValue: workflow.desc })}</p>
 </div>
 <input type="checkbox" checked={workflow.enabled} readOnly className="rounded" />
 </div>
 </div>
 ))}

 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <div>
 <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Approval Levels')}</label>
 <div className="px-3 py-2 border border-border rounded-md bg-muted/40 text-muted-foreground">
 2 Levels (Manager + HR)
 </div>
 </div>
 <div>
 <label className="block text-sm font-medium text-muted-foreground mb-2">{t('Escalation Days')}</label>
 <input type="number" value={3} readOnly className="w-full px-3 py-2 border border-border rounded-md bg-muted/40 text-muted-foreground" />
 </div>
 </div>
 </div>
 </div>
 );

 default:
 return null;
 }
 };

 return (
 <div className="p-6 max-w-7xl mx-auto">
 {/* Page Header */}
 <div className="bg-card rounded-xl shadow-sm border border-border p-6 mb-6">
 <div className="flex items-center gap-4">
 <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
 <Settings size={24} color="white" />
 </div>
 <div>
 <h1 className="text-2xl font-semibold text-foreground tracking-tight">{t('Core HR Settings')}</h1>
 <p className="text-muted-foreground mt-1">{t('Configure custom fields and HR operations for your company')}</p>
 </div>
 </div>
 </div>

 {/* Tab Navigation */}
 <div className="bg-card rounded-xl shadow-sm border border-border p-4 mb-6">
 <div className="flex items-center space-x-1 overflow-x-auto">
 {settingsTabs.map((tab) => {
 const TabIcon = tab.icon;
 const isActive = activeTab === tab.id;
 return (
 <button
 key={tab.id}
 onClick={() => setActiveTab(tab.id)}
 className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
 isActive
 ? 'bg-primary/10 text-primary border border-primary/20'
 : 'text-muted-foreground hover:text-foreground hover:bg-muted'
 }`}
 >
 <TabIcon size={16} className={isActive ? 'text-primary' : 'text-muted-foreground'} />
 <span>{t(tab.label)}</span>
 {tab.comingSoon && (
 <span className="ms-1 inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
 {t('Soon')}
 </span>
 )}
 </button>
 );
 })}
 </div>
 </div>

 {/* Preview note for the not-yet-functional tabs */}
 {activeIsComingSoon && (
 <div className="mb-4 flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
 <Clock size={16} className="shrink-0" />
 {t('This section is a preview. Configuration here is not saved yet.')}
 </div>
 )}

 {/* Tab Content */}
 <div className="mb-6">
 {renderTabContent()}
 </div>
 </div>
 );
}
